import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, Transition as _Transition, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-261c7781"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  class: "mb-2",
  "data-int": "order-cell-address-modal-title"
}
const _hoisted_2 = ["title"]
const _hoisted_3 = { class: "columns" }
const _hoisted_4 = { class: "column" }
const _hoisted_5 = ["disabled"]

import AddressForm from '@/components/library/orders/cells/address/AddressForm.vue'
import BaseModal from '@/components/common/modal/BaseModal.vue'
import BTooltip from '@/buefy/src/components/tooltip/Tooltip.vue'
import i18n from '@/locales'
import SuggestedAddress from '@/components/library/orders/cells/address/SuggestedAddress.vue'
import SubpremisePrompt from '@/components/library/orders/cells/address/SubpremisePrompt.vue'
import { Address, AddressInterface } from '@/interfaces/admin/address'
import { AddressStatus } from '@/interfaces/admin/places'
import { computed, PropType, ref } from 'vue'
import { debounce } from 'lodash'
import { validate } from '@/controllers/global/places'


export default /*#__PURE__*/_defineComponent({
  __name: 'AddressVerificationModal',
  props: {
  address: {
    type: Object as PropType<AddressInterface | null>,
    required: false,
    default: null
  },
  initialAddressStatus: {
    type: String as () => AddressStatus | null,
    default: null
  },
  isRequired: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: ''
  },
  sessionToken: {
    type: String,
    required: true
  }
},
  emits: ['save', 'close'],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const addressFormRef = ref<InstanceType<typeof AddressForm> | null>(null);
const addressStatus = ref<AddressStatus | null>(props.initialAddressStatus);
const currentAddress = ref<Address|null>(!!props.address ? new Address(props.address) : null);
const isFormValid = ref(false);
const isValidating = ref(false);

/**
 * State to present different messages and subcomponents
 */
const modalState = computed(() => {
  if (!currentAddress.value) return 'incomplete';

  // Respect explicit address status states
  if (addressStatus.value === AddressStatus.ADD_SUBPREMISE) return 'missingSubpremise';
  if (addressStatus.value === AddressStatus.VERIFIED) return 'verified';

  // For UNVERIFIED or null status, check completeness
  const { line1, city, countryCode } = currentAddress.value;
  var isComplete: boolean;
  switch (countryCode) {
    case 'USA':
      isComplete = !!(line1 && city && countryCode);
      break;
    default:
      isComplete =  !!(line1 && countryCode);
  }
  return isComplete ? 'unverified' : 'incomplete';
});

const addressStateConfig = computed(() => {
  const config = {
    incomplete: {
      color: 'has-text-danger',
      icon: 'fas fa-times-circle',
      label: i18n.global.t('addressState.incomplete'),
      tooltipColor: 'is-danger'
    },
    missingSubpremise: {
      color: 'has-text-warning',
      icon: 'fas fa-exclamation-circle',
      label: i18n.global.t('addressState.missingSubpremise'),
      tooltipColor: 'is-warning'
    },
    unverified: {
      color: 'has-text-warning',
      icon: 'fas fa-exclamation-circle',
      label: i18n.global.t('addressState.unverified'),
      tooltipColor: 'is-warning'
    },
    verified: {
      color: 'has-text-success',
      icon: 'fas fa-check-circle',
      label: i18n.global.t('addressState.verified'),
      tooltipColor: 'is-success'
    }
  };
  return config[modalState.value];
});

const close = () => emit('close');

const onFormUpdate = (payload: { address: Address, isFormValid: boolean }) => {
  currentAddress.value = payload.address;
  isFormValid.value = payload.isFormValid;

  if (
    addressStatus.value == AddressStatus.UNVERIFIED &&
    payload.address.lengthWithoutCountryCode() > 3 &&
    payload.isFormValid
  ) {
    validateAddressDebounced();
  } else {
    addressStatus.value = AddressStatus.UNVERIFIED;
  }
};

const onSuggestionSelected = (addressData: AddressInterface) => {
  // Populate the form with the address data from the selected place
  if (addressFormRef.value) {
    addressFormRef.value.populateFromAddress(addressData);
    // Validate the suggested address
    currentAddress.value = new Address(addressData);
    validateAddressDebounced();
  }
};

const save = () => {
  if (isFormValid.value) {
    emit('save', {
      address: currentAddress.value,
      addressStatus: addressStatus.value
    });
    close();
  }
};

const validateAddress = async () => {
  if (!currentAddress.value) return;

  isValidating.value = true;
  try {
    const response = await validate(currentAddress.value, props.sessionToken);
    switch (response.status) {
      case AddressStatus.ADD_SUBPREMISE:
        addressStatus.value = AddressStatus.ADD_SUBPREMISE;
        break;
      case AddressStatus.VERIFIED:
        addressStatus.value = AddressStatus.VERIFIED;
        break;
      case AddressStatus.UNVERIFIED:
      default:
        addressStatus.value = AddressStatus.UNVERIFIED;
        break;
    }
  } catch (error) {
    console.error('Failed to validate address:', error);
    /**
     * Short of an API outage, unsupported addresses would be the most common error.
     * And this will default to UNVERIFIED.
     */
    addressStatus.value = AddressStatus.UNVERIFIED;
  } finally {
    isValidating.value = false;
  }
};

const validateAddressDebounced = debounce(validateAddress, 1000);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseModal, {
    "data-int": "order-cell-address-modal",
    onClose: close
  }, {
    title: _withCtx(() => [
      _createElementVNode("p", _hoisted_1, [
        _createTextVNode(_toDisplayString(__props.title || _ctx.$tc('address', 2)) + " ", 1),
        _createVNode(BTooltip, {
          position: "is-bottom",
          label: addressStateConfig.value.label,
          type: addressStateConfig.value.tooltipColor
        }, {
          default: _withCtx(() => [
            _createElementVNode("span", {
              class: _normalizeClass(["icon", addressStateConfig.value.color]),
              title: addressStateConfig.value.label
            }, [
              _createElementVNode("i", {
                class: _normalizeClass(addressStateConfig.value.icon)
              }, null, 2)
            ], 10, _hoisted_2)
          ], undefined, true),
          _: 1
        }, 8, ["label", "type"])
      ])
    ]),
    body: _withCtx(() => [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_Transition, { name: "fade-width" }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(AddressForm, {
                ref_key: "addressFormRef",
                ref: addressFormRef,
                address: currentAddress.value,
                "is-required": __props.isRequired,
                onUpdate: onFormUpdate
              }, null, 8, ["address", "is-required"])
            ])
          ], undefined, true),
          _: 1
        }),
        _createVNode(_Transition, { name: "expand" }, {
          default: _withCtx(() => [
            (addressStatus.value === _unref(AddressStatus).ADD_SUBPREMISE)
              ? (_openBlock(), _createBlock(SubpremisePrompt, {
                  key: 0,
                  class: "column",
                  "address-status": addressStatus.value,
                  "current-line2": currentAddress.value?.line2 || ''
                }, null, 8, ["address-status", "current-line2"]))
              : (_openBlock(), _createBlock(SuggestedAddress, {
                  key: 1,
                  class: "column",
                  address: currentAddress.value,
                  "address-status": addressStatus.value,
                  "session-token": props.sessionToken,
                  onSuggestionSelected: onSuggestionSelected
                }, null, 8, ["address", "address-status", "session-token"]))
          ], undefined, true),
          _: 1
        })
      ])
    ]),
    actions: _withCtx(() => [
      _createElementVNode("button", {
        "data-int": "order-cell-address-modal-cancel-button",
        class: "button",
        onClick: close
      }, _toDisplayString(_ctx.$t('cancel')), 1),
      _createElementVNode("button", {
        class: "button is-primary",
        "data-int": "order-cell-address-modal-save-button",
        disabled: addressStatus.value !== _unref(AddressStatus).VERIFIED && !isFormValid.value,
        onClick: save
      }, _toDisplayString(_ctx.$t('save')), 9, _hoisted_5)
    ]),
    _: 1
  }))
}
}

})